import { useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';

import ErrorText from '../errortext';
import SchoolIcon from '../../assets/icons/AccountManagementIcons/school_icon.svg';
import auth from '../../utils/auth';
import { useSchoolGroups } from 'hooks/data/useSchoolGroups';
import { StyledErrorText } from 'components/Login/LoginForm';
import { MultiSelect, StyledSelect } from 'ui-kit';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { USStates } from '../../utils/enums';
import { getErrorStyles } from '../../utils/styles';
import { Alert, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ATSSchoolAccountModalTestIds } from 'data-testids/ATS';
import { triggerDataChangeRequestSurvey } from '../../utils/posthogSurveys';
import { Chip } from 'sharedComponents/Chip';

const requiredFields = {
  name: 'School name is required',
  location: 'Street address is required',
  city: 'City is required',
  state: 'State is required',
};

const FormInput = ({
  fieldName,
  placeholder,
  type = 'text',
  value,
  onChange,
  errors,
  validationErrors,
  theme,
}) => {
  const hasError = validationErrors[fieldName];
  return (
    <>
      <div className="account-modal-dropshadow" style={getErrorStyles(hasError, theme)}>
        <input
          className="account-modal-input"
          style={getErrorStyles(hasError, theme)}
          name={fieldName}
          type={type}
          placeholder={placeholder}
          required
          value={value || ''}
          onChange={onChange}
        />
      </div>
      <ErrorText message={errors[fieldName] || validationErrors[fieldName]} />
    </>
  );
};

const SchoolAccountModal = (props) => {
  const theme = useTheme();
  const schoolGroups = useSchoolGroups();
  const [schoolState, setSchoolState] = useState({ ...props.school });
  const [validationErrors, setValidationErrors] = useState({});
  const USStateOptions = useMemo(
    () => USStates().map((state) => ({ value: state.value, label: state.label })),
    []
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    const errors = {};
    Object.keys(requiredFields).forEach((field) => {
      if (!schoolState[field]?.trim()) {
        errors[field] = requiredFields[field];
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    props.onSave(schoolState);
    return false;
  };

  const updateField = (fieldName, value) => {
    setSchoolState((prevState) => ({ ...prevState, [fieldName]: value }));
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  const setSchoolGroups = (schoolGroupIds) => {
    setSchoolState((prevState) => ({ ...prevState, school_groups: schoolGroupIds }));
  };

  const schoolGroupChoices = schoolGroups.data.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const EDITMODE = !!props.school.id;

  const isConnectState = () => {
    const connectStatesSet = new Set(['IN', 'OK', 'AR', 'TN', 'MO']);
    const userState = auth.getUser()?.profile?.district?.state;
    return connectStatesSet.has(userState);
  };

  const isDistrictOnHire = auth.isAtsLiteDistrict() || auth.isATSFullDistrict();
  const doesNotHaveNCESId = !props.school.school_nces_id;
  const shouldShowDiscoverWarning = isConnectState() && isDistrictOnHire && doesNotHaveNCESId;

  const handleRequestChange = () => {
    const user = auth.getUser();
    props.onHide();
    triggerDataChangeRequestSurvey({
      requester_districtId: user?.profile?.district?.id,
      requester_districtName: user?.profile?.district?.name,
      target_schoolId: props.school.id,
      target_schoolName: props.school.name,
      target_schoolNcesId: props.school.school_nces_id,
      requestType: 'nces_assignment',
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Modal show={props.displayModal} bsSize="large" dialogClassName="account-modal">
        <Modal.Header>
          <img src={SchoolIcon} alt="" />
          <h3>{EDITMODE ? 'Edit' : 'New'} School</h3>
        </Modal.Header>
        <Modal.Body>
          <ErrorText message={props.errors.non_field_errors} />
          <FormFieldSection>
            {shouldShowDiscoverWarning && (
              <Alert
                style={{ marginBottom: '24px' }}
                severity="warning"
                data-testid={ATSSchoolAccountModalTestIds.WARNING}
              >
                If you would like this school to have teaching roles shown on the statewide job
                board,{' '}
                <Tooltip title="Click to request that Nimble link this school to NCES data">
                  <Chip
                    label="Request Change"
                    icon={<EditOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                    clickable={true}
                    variant="outlined"
                    size="small"
                    dataTestId={ATSSchoolAccountModalTestIds.REQUEST_CHANGE_BUTTON}
                    onClickFunction={handleRequestChange}
                    sx={{
                      display: 'inline-flex',
                      verticalAlign: 'middle',
                      cursor: 'pointer',
                      height: '28px',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '& .MuiChip-label': {
                        padding: '0 10px 0 6px',
                      },
                      '& .MuiChip-icon': {
                        color: 'primary.main',
                        marginLeft: '8px',
                        marginRight: '-2px',
                      },
                      '&:hover': {
                        backgroundColor: 'primary.fill',
                        borderColor: 'primary.dark',
                      },
                    }}
                  />
                </Tooltip>{' '}
                or contact support@hirenimble.com for assistance.
              </Alert>
            )}
            <h4 className="section-title">School Name</h4>
            <FormInput
              fieldName="name"
              placeholder="School Name"
              value={schoolState.name}
              onChange={(event) => updateField('name', event.target.value)}
              errors={props.errors}
              validationErrors={validationErrors}
              theme={theme}
            />
          </FormFieldSection>

          <h4 className="section-title">Location</h4>
          <FormFieldSection>
            <StyledSubLabel variant="subtitle1">Street Address</StyledSubLabel>
            <div
              className="account-modal-dropshadow"
              style={{
                marginBottom: validationErrors.location && '0px',
                ...getErrorStyles(validationErrors.location, theme),
              }}
            >
              <input
                className="account-modal-input"
                style={getErrorStyles(validationErrors.location, theme)}
                name="location"
                type="text"
                placeholder="Street Address (ex: 1234 Main St)"
                required
                value={schoolState.location || ''}
                onChange={(event) => updateField('location', event.target.value)}
              />
            </div>
            <ErrorText message={props.errors.location || validationErrors.location} />
          </FormFieldSection>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 70%' }}>
              <FormFieldSection>
                <StyledSubLabel variant="subtitle1">City</StyledSubLabel>
                <FormInput
                  fieldName="city"
                  placeholder="City"
                  value={schoolState.city}
                  onChange={(event) => updateField('city', event.target.value)}
                  errors={props.errors}
                  validationErrors={validationErrors}
                  theme={theme}
                />
              </FormFieldSection>
            </div>
            <div style={{ flex: '0 0 calc(30% - 1rem)' }}>
              <StyledSubLabel variant="subtitle1">State</StyledSubLabel>
              <FormFieldSection>
                <div>
                  <div style={getErrorStyles(validationErrors.state, theme)}>
                    <StyledSingleSelect
                      hasError={!!validationErrors.state}
                      theme={theme}
                      boxShadow="0 3px 3px 0 rgba(0, 0, 0, 0.08)"
                      name="school-states"
                      options={USStateOptions}
                      required
                      defaultValue={USStateOptions.find((school) => {
                        return schoolState?.state?.toLowerCase() === school.label?.toLowerCase();
                      })}
                      onChange={(selectedOption) => {
                        updateField('state', selectedOption?.label || '');
                      }}
                      placeholder="Select a state"
                    />
                  </div>
                  {validationErrors.state && (
                    <ErrorText message={validationErrors.state} className="state-dropdown" />
                  )}
                </div>
              </FormFieldSection>
            </div>
          </div>
          {!validationErrors.location &&
            !validationErrors.city &&
            !schoolState.geocoded_location &&
            EDITMODE && (
              <StyledErrorText style={{ marginTop: '0px', marginBottom: '1rem' }}>
                Please enter a valid location and ensure your district location information is
                correct
              </StyledErrorText>
            )}
          {/* Only district/super admins can tag schools */}
          {auth.isDistrictAdmin() && !auth.inPostingOnlyDistrict() && (
            <>
              <h4 className="section-title">Groups</h4>
              <div>
                <StyledMultiSelect
                  border={`0.5px solid ${theme.palette.gray.light}`}
                  boxShadow="0 3px 3px 0 rgba(0, 0, 0, 0.08)"
                  name="school-groups"
                  multi={true}
                  options={schoolGroupChoices}
                  defaultValue={schoolGroupChoices.filter((school) =>
                    schoolState.school_groups?.includes(school.value)
                  )}
                  onChange={(selectedOptions) => {
                    selectedOptions = selectedOptions ?? [];
                    setSchoolGroups(selectedOptions.map(({ value }) => value));
                  }}
                  placeholder="Start typing to add this school to one or more groups"
                />
                <ErrorText message={props.errors.schoolGroups} />
              </div>
            </>
          )}
          {EDITMODE && auth.isSuperAdminUser() && (
            <div className="deactivate" onClick={props.openArchiveModal}>
              {/* use this.props instead of this.state here in case they edit the name */}
              Archive {props.school.name}
            </div>
          )}
          <div className="action-buttons-div">
            <button className="cancel" onClick={props.onHide}>
              Cancel
            </button>
            <button className="save" onClick={handleSubmit}>
              Save
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </form>
  );
};

SchoolAccountModal.propTypes = {
  school: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  openArchiveModal: PropTypes.func,
};

export default SchoolAccountModal;

const StyledMultiSelect = styled(MultiSelect)`
  margin: 1rem 0 2rem;
`;

const StyledSingleSelect = styled(StyledSelect)`
  margin: 1rem 0 0.5rem;

  ${(props) =>
    props.hasError &&
    `
    background-color: ${props.theme.palette.error.fill};
    margin-bottom: 1rem;
    `}
  & > div {
    background-color: ${(props) =>
      props.hasError ? props.theme.palette.error.fill : props.theme.palette.white.main};
    border: ${(props) =>
      props.hasError
        ? `1px solid ${props.theme.palette.error.main}`
        : `1px solid ${props.theme.palette.gray.light}`};
  }
`;

const StyledSubLabel = styled(Typography)`
  margin-top: 0 !important;
  margin-bottom: -1rem !important;
`;

const FormFieldSection = styled('section')`
  margin-bottom: 2rem !important;
`;
